/**
 * @packageDocumentation
 * @module dom/element
 */

/**
 * query all parents and their children, excepting shadowDOM (note: could exclude massive portions of the opposite-side of the DOM)
 *
 * @param query the string which describes the element to find; passed to `querySelector`
 * @return the element found (or null)
 */
function querySelectorParentSiblings(query: string, root: Element) {
  let element;
  let max = 9999;

  if (!query) {
    return null;
  }

  let _root: Node | HTMLElement | null = root;
  let _rootLast: Node | HTMLElement | null = null;
  while (_root && _rootLast !== _root && max > 0) {
    _rootLast = _root;
    _root = _root.parentElement || root.parentNode;
    if (!_root) {
      break;
    }

    element = _root['host'] && `#${_root['host'].id}` === query ? _root['host'] : (_root as any).querySelector(query);
    if (element) {
      return element;
    }

    max -= 1;
  }

  return null;
}

export interface QueryAll {
  /**
   * Query the DOM by ID, Query, or search ShadowDOM
   * @param query the tag name we are verifying
   * @param root a tag
   * @return true if Node, false otherwise
   */
  (query: string, root: HTMLElement): Element[];
}

/**
 * { @link QueryAll }
 */
export default (query: string, root?: HTMLElement): Element[] => {
  try {
    if (!query) {
      return [];
    }

    query = query.trim();

    const elementById = document.getElementById(query);
    if (elementById) {
      return [elementById];
    }

    const searchUnderEl = root || document;
    const documentQuery = searchUnderEl.querySelectorAll(query);
    if (documentQuery && documentQuery.length > 0) {
      return Array.from(documentQuery);
    }

    const elementQuery = querySelectorParentSiblings(query, root || document.body);
    if (elementQuery) {
      return [elementQuery];
    }

    return [];
  } catch (e) {
    return [];
  }
};
